import React, { useState } from 'react';
import { demoPublicService } from '../../services/demoApi';
import logo from '../../assets/jeta-logo.png';
import '../TrialRequest/TrialRequest.css'; // shares the public-page look (scoped under .trial-page)

// Public, no-login RollShopXpert demo request page — reachable at /demo-request.
// Single step: details -> confirmation. Mirrors the server-side checks in demoPublic.controller.js.

const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const PHONE_RE = /^[0-9+()\-.\s]{7,20}$/;
const DEPLOYMENTS = [['not_sure', 'Not sure yet'], ['cloud', 'Cloud'], ['on_premise', 'On-premise']];

const emptyForm = () => ({
  contactName: '', designation: '', company: '', email: '', phone: '', deployment: 'not_sure', message: '',
  website: '', // honeypot: hidden from people, bots tend to fill it
});

export default function DemoRequest() {
  const [form, setForm] = useState(emptyForm);
  const [invalid, setInvalid] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const inv = (k) => (invalid[k] ? 'invalid' : undefined);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const labels = { contactName: 'Your name', company: 'Company / Mill name', email: 'Email address', phone: 'Phone number' };
    const bad = Object.keys(labels).filter(k => !form[k].trim());
    const bads = { ...Object.fromEntries(bad.map(k => [k, true])) };
    let msg = '';
    if (bad.length) msg = 'Fill in: ' + bad.map(k => labels[k]).join(', ') + '.';
    else if (form.email.trim().length > 254 || !EMAIL_RE.test(form.email.trim())) { msg = 'Enter a valid email address, like you@company.com.'; bads.email = true; }
    else if (!PHONE_RE.test(form.phone.trim())) { msg = 'Enter a valid phone number.'; bads.phone = true; }
    setInvalid(bads); setError(msg);
    if (msg) return;

    setBusy(true);
    try {
      const res = await demoPublicService.submit({ ...form });
      setDone({ reference: res.data.reference, email: form.email.trim() });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const restart = () => { setForm(emptyForm()); setInvalid({}); setError(''); setDone(null); };

  return (
    <div className="trial-page">
      <header className="band"><div className="wrap">
        <div className="brand-row">
          <img src={logo} alt="JetaTech – Solutions for Business" className="brand-logo" />
          <div className="brand">RollShopXpert</div>
        </div>
        <h1>Book your demo</h1>
        <p>Tell us about your mill and we will get in touch to arrange a RollShopXpert walkthrough tailored to your plant.</p>
      </div></header>

      <main><div className="wrap">
        {!done ? (
          <form className="panel" noValidate onSubmit={submit}>
            <div className="grid">
              <label className="f"><span>Your name *</span><input type="text" className={inv('contactName')} value={form.contactName} onChange={set('contactName')} placeholder="Rajesh Kumar" maxLength={120} autoComplete="name" /></label>
              <label className="f"><span>Designation</span><input type="text" value={form.designation} onChange={set('designation')} placeholder="Plant Manager" maxLength={120} autoComplete="organization-title" /></label>
              <label className="f"><span>Company / Mill name *</span><input type="text" className={inv('company')} value={form.company} onChange={set('company')} placeholder="ABC Steels Pvt. Ltd." maxLength={200} autoComplete="organization" /></label>
              <label className="f"><span>Email address *</span><input type="email" className={inv('email')} value={form.email} onChange={set('email')} placeholder="you@company.com" autoComplete="email" /></label>
              <label className="f"><span>Phone number *</span><input type="tel" className={inv('phone')} value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" autoComplete="tel" /></label>
              <label className="f"><span>Preferred deployment</span>
                <select value={form.deployment} onChange={set('deployment')}>
                  {DEPLOYMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="f full"><span>Tell us about your mill</span><textarea value={form.message} onChange={set('message')} maxLength={1500} placeholder="Number of stands, current challenges, plant location..." /></label>
              {/* Honeypot — off-screen and skipped by keyboard/AT users */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></label>
              </div>
            </div>
            {error && <div className="err">{error}</div>}
            <div className="actions">
              <span className="hint">Fields marked * are required.</span>
              <button className="primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Request a demo'}</button>
            </div>
          </form>
        ) : (
          <section className="panel">
            <h2>Demo request received</h2>
            <p className="notice">Thank you. We have sent a confirmation to <strong>{done.email}</strong> and our team will contact you shortly to schedule your demo.{done.reference && <> Your reference is <code>{done.reference}</code>.</>}</p>
            <div className="actions"><span className="hint">Quote your reference in any correspondence.</span><button type="button" className="ghost" onClick={restart}>Submit another request</button></div>
          </section>
        )}

        <footer style={{ marginTop: 20 }}>JetaTech · Questions: <span style={{ userSelect: 'all' }}>subscriptions@jetatech.in</span></footer>
      </div></main>
    </div>
  );
}
